<template>
  <BaseModal :title="'Create Storage'" @close="closeModal">
    <template #modalContent>
      <div class="modal-content-wrapper">
        <div>
          <h3 class="modal-heading text-xl font-bold text-white p-2">Add new storage</h3>
        </div>
        <div class="flex items-start justify-start h-full w-full">
          <div class="bg-[#272727] text-white-lg p-6 w-full max-w-lg">
            <!-- Loading State -->
            <div v-if="isLoading" class="text-center">
              Loading providers...
            </div>
            <!-- Storage Name -->
            <div v-else class="mb-4">
              <label class="block text-sm font-medium mb-2">Storage Name</label>
              <input v-model="form.name" type="text" class="w-full bg-[#333333] text-dark-black p-2"
                placeholder="e.g., My Cloud Storage" />
            </div>

            <!-- Provider Selection -->
            <!-- <div v-if="!isLoading" class="mb-4">
              <label class="block text-sm font-medium mb-2">Provider</label>
              <select v-model="form.selectedProviderId" class="w-full bg-[#333333] text-dark-black p-2"
                @change="fetchProviderDetails">
                <option value="" disabled>Select Provider</option>
                <option v-for="provider in providers" :key="provider.id" :value="provider.id">
                  {{ provider.name }}
                </option>
              </select>
            </div> -->

            <!-- Custom Dropdown for Provider Selection -->
            <div v-if="!isLoading" class="relative w-full mb-4">
              <label class="block text-sm font-medium mb-2">Provider</label>
              <div @click="toggleDropdown" style="border:1px solid white !important;"
                class="h-12 w-full p-2 text-lg bg-black text-white flex justify-between items-center cursor-pointer">
                <div class="flex gap-4 items-center justify-start">
                  <img v-if="selectedProvider" class="w-6 h-6 object-contain" :src="selectedProvider.image" />
                  <span><span>
                      {{ selectedProvider ? selectedProvider.name : 'Select Provider' }}
                    </span> -
                    <span class="capitalize">{{ selectedProvider ? selectedProvider.type
                      : ''
                    }}</span></span>
                </div>
                <span>
                  <DownButton />
                </span>
              </div>

              <div v-if="isOpen" class="absolute w-full bg-dark-black text-white z-10 mt-1"
                style="border:1px solid white !important;">
                <div v-for="provider in providers" :key="provider.id" @click="selectProvider(provider)"
                  class="p-2 px-4 h-12 flex gap-4 items-center justify-start cursor-pointer text-lg hover:bg-gray-800 hover:text-green-500">
                  <img class="w-6 h-6 object-contain" :src="provider.image" />
                  <span><span>{{ provider.name }}</span> - <span class="capitalize">
                      {{ provider.type }}
                    </span></span>
                </div>
              </div>
            </div>


            <!-- Storage Configuration Form -->
            <div v-if="selectedProvider && !isLoading">
              <div class="mb-4" v-if="selectedProvider.selfHostAvailable">
                <label class="flex items-center gap-2">
                  <input type="checkbox" v-model="form.selfHost" class="bg-[#333333]" @change="updateOptions" />
                  Self-hosted
                </label>
              </div>

              <div v-if="form.selfHost || selectedProvider.name === 'AWS S3'" class="mb-4">
                <p class="text-md text-blue-500 border-dotted p-2" v-html="`//Note: ${selectedProvider.note}`"></p>
              </div>

              <!-- Dynamic Options Fields (shown only when selfHost is true) -->
              <div v-if="form.selfHost || selectedProvider.name === 'AWS S3'" v-for="option in selectedProvider.options"
                :key="option.field" class="mb-4">

                <label class="block text-sm font-medium mb-2">{{ option.title }}</label>
                <input v-model="form.options[option.field]" :type="option.type === 'string' ? 'text' : option.type"
                  class="w-full bg-[#333333] text-dark-black p-2" :placeholder="`Enter ${option.title}`" />
              </div>
              <!-- <div class="mb-4">
                <label class="flex items-center gap-2">
                  <input type="checkbox" v-model="form.deActive" class="bg-[#333333]" />
                  Disabled
                </label>
              </div> -->
            </div>

            <!-- Validation and Save Buttons -->
            <div v-if="!isLoading" class="flex justify-end gap-2">
              <button v-if="form.selfHost"
                class="bg-transparent outline-none border-white cursor-pointer text-yellow py-2 px-4 hover:bg-gray-600 disabled:cursor-not-allowed"
                @click="validateConnection" :disabled="!form.options.nodeUrl"
                :style="{ opacity: form.options.nodeUrl ? 1 : 0.5 }">
                >>Validate
              </button>
              <button
                class="bg-transparent outline-none border-white cursor-pointer text-primary py-2 px-4 hover:bg-[#b0b81c]"
                @click="saveStorage" :disabled="!isFormValid || (form.selfHost && !isValidated)"
                :style="{ opacity: isFormValid && (!form.selfHost || isValidated) ? 1 : 0.8 }">
                >>Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from "@/components/common/BaseModal.vue";
import api from '@/http/api/api.js'
import DownButton from "../../decast/components/common/DownButton.vue";
import constants from "../../../../../constant";

export default {
  name: 'CreateStorageModal',
  props: {
    closeModal: {
      type: Function,
      required: true,
    },
    fetchStorages: {
      type: Function,
      required: true,
    },
  },
  components: {
    BaseModal,
    DownButton,
  },
  data() {
    return {
      isLoading: false,
      providers: [],
      form: {
        name: '',
        selectedProviderId: '',
        selfHost: false,
        options: {
          nodeUrl: '',
          apiPassword: '',
        },
        deActive: false,
      },
      isValidated: false,
      isOpen: false,
      selectedProvider: null,
    };
  },
  computed: {
    isFormValid() {
      if (!this.form.name || !this.form.selectedProviderId) {
        return false;
      }
      if (this.form.selfHost && this.selectedProvider?.options) {
        return this.selectedProvider.options.every(
          (opt) => opt.field === 'nodeUrl' || this.form.options[opt.field]
        );
      }
      return true;
    },
  },
  async created() {
    await this.fetchProviders();
  },
  methods: {
    async fetchProviders() {
      this.isLoading = true;
      try {
        const response = await api.get(`${constants.apiCastUrl}/api/v1/storageproxy/providers`);
        this.providers = response.data.data || response.data;
      } catch (error) {
        console.error('Error fetching providers:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load providers. Please try again.',
          color: 'danger',
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchProviderDetails() {
      this.form.options = { nodeUrl: '', apiPassword: '' };
      this.isValidated = false;
      this.selectedProvider = null;

      if (this.form.selectedProviderId) {
        try {
          const response = await api.get(
            `${constants.apiCastUrl}/api/v1/storageproxy/providers/${this.form.selectedProviderId}`
          );
          this.selectedProvider = response.data.data || response.data;
        } catch (error) {
          console.error('Error fetching provider details:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load provider details. Please try again.',
            color: 'danger',
          });
        }
      }
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    selectProvider(provider) {
      this.selectedProvider = provider;
      this.form.selectedProviderId = provider.id;
      this.form.selfHost = false;
      this.form.options = {};
      this.isValidated = false;
      this.isOpen = false;
    },
    updateOptions() {
      if (!this.selectedProvider) return;
      const newOptions = {};
      this.selectedProvider.options.forEach((opt) => {
        newOptions[opt.field] = this.form.selfHost ? (this.form.options[opt.field] || '') : '';
      });
      this.form.options = newOptions;
    },
    async validateConnection() {
      if (!this.form.options.nodeUrl || !this.form.options.apiPassword) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please enter both Node URL and API Password before validating!',
          color: 'danger',
        });
        return;
      }

      try {
        console.log('Validating connection:', this.form.options);
        // Replace with actual validation API call if available
        await new Promise((resolve) => setTimeout(resolve, 1000));
        this.isValidated = true;
        this.$vs.notify({
          title: 'Success',
          text: 'Connection validated successfully!',
          color: 'success',
        });
      } catch (error) {
        this.isValidated = false;
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to validate connection.',
          color: 'danger',
        });
      }
    },
    async saveStorage() {
      if (!this.isFormValid) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please complete the form before saving!',
          color: 'danger',
        });
        return;
      }

      if (this.form.selfHost && !this.isValidated) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please validate the connection before saving!',
          color: 'warning',
        });
        return;
      }

      try {
        // Send POST request to create storage
        const response = await api.post(`${constants.apiCastUrl}/api/v1/storageproxy/storages`, {
          name: this.form.name,
          provider: this.selectedProvider, // Send full provider object
          options: this.form.selfHost ? this.form.options : {},
          selfHost: this.form.selfHost,
          deActive: this.form.deActive,
          status: this.form.selfHost ? this.isValidated : false,
        });

        const newStorage = response.data;

        // Emit new storage data to StorageSection.vue
        this.$emit('add-storage', {
          id: newStorage.id,
          name: this.form.name,
          provider: this.selectedProvider ? this.selectedProvider.name : '',
          options: this.form.selfHost ? { ...this.form.options } : {},
          selfHost: this.form.selfHost,
          deActive: this.form.deActive,
          status: this.form.selfHost ? this.isValidated : false,
        });

        this.$vs.notify({
          title: 'Success',
          text: 'Storage created successfully!',
          color: 'success',
        });

        this.fetchStorages();

        // Reset form
        this.form = {
          name: '',
          selectedProviderId: '',
          selfHost: false,
          options: { nodeUrl: '', apiPassword: '' },
          deActive: false,
        };
        this.isValidated = false;
        this.selectedProvider = null;
        this.closeModal();
      } catch (error) {
        console.error('Error creating storage:', error);
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to create storage. Please try again.',
          color: 'danger',
        });
      }
    },
  }
}
</script>

<style scoped>
input,
select {
  border: 1px solid #333333;
}

input:focus,
select:focus {
  outline: none;
  border-color: #d7df23;
}
</style>